import { Fragment } from 'react';
import { useRecoilValue } from 'recoil';

import * as K from 'common/constants';
import { AvailableTheme } from 'common/types';
import { themeState } from 'components/common/Managers/SettingsManager.state';

import Page from 'components/common/Page/Page';

import BadgeExample from './BadgeExample';
import ButtonExample from './ButtonExample';
import CardExample from './CardExample';
import CheckboxRadioExample from './CheckboxRadioExample';
import ChooserExample from './ChooserExample';
import ClearableInputExample from './ClearableInputExample';
import ColorExampleSentinelOne from './ColorExampleSentinelOne';
import ColorExampleSentinelOneDark from './ColorExampleSentinelOneDark';
import DialogExample from './DialogExample';
import DropdownExample from './DropdownExample';
import IconButtonExample from './IconButtonExample';
import LoadingPlaceholderExample from './LoadingPlaceholderExample';
import PageAlertExample from './PageAlertExample';
import PageHeaderExample from './PageHeaderExample';
import SwitchExample from './SwitchExample';
import TableExample from './TableExample';
import TabsExample from './TabsExample';
import ToolbarExample from './ToolbarExample';
import TreeExample from './TreeExample';
import TypographyExample from './TypographyExample';

import './ExamplesPage.scss';

/**
 * This page shows working examples of Scalyr components and shared styles and patterns
 */

function ExamplesPage(): JSX.Element {
  const theme = useRecoilValue(themeState);
  const colorsPage =
    theme === AvailableTheme.SENTINEL_ONE ? <ColorExampleSentinelOne /> : <ColorExampleSentinelOneDark />;

  const components = [
    { id: 'badge', name: 'Badge', component: <BadgeExample /> },
    { id: 'button', name: 'Button', component: <ButtonExample /> },
    { id: 'card', name: 'Card', component: <CardExample /> },
    { id: 'checkboxRadio', name: 'Checkbox/Radio Button', component: <CheckboxRadioExample /> },
    { id: 'chooser', name: 'Chooser', component: <ChooserExample /> },
    { id: 'clearableInput', name: 'Clearable Input', component: <ClearableInputExample /> },
    { id: 'colors', name: 'Colors', component: colorsPage },
    { id: 'dialog', name: 'Dialog', component: <DialogExample /> },
    { id: 'dropdown', name: 'Dropdown', component: <DropdownExample /> },
    { id: 'iconButton', name: 'Icon Button', component: <IconButtonExample /> },
    { id: 'loadingPlaceholder', name: 'Loading Placeholder', component: <LoadingPlaceholderExample /> },
    { id: 'pageAlert', name: 'Page Alert', component: <PageAlertExample /> },
    { id: 'pageHeader', name: 'Page Header', component: <PageHeaderExample /> },
    { id: 'switch', name: 'Switch', component: <SwitchExample /> },
    { id: 'table', name: 'Table', component: <TableExample /> },
    { id: 'tabs', name: 'Tabs', component: <TabsExample /> },
    { id: 'toolbar', name: 'Toolbar', component: <ToolbarExample /> },
    { id: 'tree', name: 'Tree', component: <TreeExample /> },
    { id: 'typography', name: 'Typography', component: <TypographyExample /> },
  ];

  return (
    <Page docTitle="Components" type="examples">
      {/* Left Menu */}
      <div className="ExamplesPage__Menu">
        <div className="ExamplesPage__MenuHeader">Components</div>
        <ul className="nav flex-column">
          {components.map(({ id, name }) => (
            <li className="nav-item" key={id}>
              <a className="nav-link" href={`#${id}`}>
                {name}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Main Page */}
      <div className="ExamplesPage__Body">
        <section className="ExamplesPage__Section ExamplesPage__Section--Header">
          <h1 className="ExamplesPage__SectionTitle">{K.PRODUCT_NAME} Design System</h1>
          <div className="ExamplesPage__Description">
            The components, styles, and standards that form the building blocks of our new React UI
          </div>
        </section>
        <>
          {components.map(({ id, component }) => (
            <Fragment key={id}>{component}</Fragment>
          ))}
        </>
      </div>
    </Page>
  );
}

export default ExamplesPage;
